import { formatNumber } from './utils';

const currencySymbols: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

export function getCurrencySymbol(currency: string | null | undefined): string {
  if (!currency) return '';
  return currencySymbols[currency] ?? currency;
}

export function formatCost(
  value: number | null | undefined,
  currency: string | null | undefined
): string {
  if (value == null) return '—';
  const symbol = (currency && currencySymbols[currency]) ?? currency ?? '';
  return `${symbol}${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Compact large numbers for chart axes and metric cards: 1500 → "1.5k", 2000000 → "2m". */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  const trim = (n: number) => (n % 1 === 0 ? n.toFixed(0) : n.toFixed(1));
  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}m`;
  if (abs >= 1_000) return `${trim(value / 1_000)}k`;
  return value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

/**
 * Emissions readout with unit — "11,427 tCO₂e", "1.84 tCO₂e", or "11.4k tCO₂e"
 * when compact. Non-compact values go through `formatNumber`, so small footprints
 * keep 2 decimal places while large ones round to whole tonnes with separators.
 */
export function formatTonnes(
  value: number | string,
  { compact = false }: { compact?: boolean } = {}
): string {
  if (typeof value !== 'number') return String(value);
  const formatted = compact ? formatCompact(value) : formatNumber(value);
  return `${formatted} tCO₂e`;
}

export function formatDate(
  date: Date | string | number | undefined,
  opts: Intl.DateTimeFormatOptions = {}
) {
  if (!date) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: opts.month ?? 'long',
      day: opts.day ?? 'numeric',
      year: opts.year ?? 'numeric',
      ...opts,
    }).format(new Date(date));
  } catch (_err) {
    return '';
  }
}
